import { FEATURE_FLAGS, type FeatureFlag } from '@/lib/features';
import { Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Props {
  feature: FeatureFlag;
}

/**
 * Override map for slugs that don't humanise cleanly via simple
 * tokenisation. Acronyms (POS, SSO, API, IP, SMS) and brand/proper-noun
 * slugs (QuickBooks, Xero, WhatsApp) live here. Anything not in the map
 * goes through the generic title-case path.
 */
const FEATURE_TITLES: Partial<Record<FeatureFlag, string>> = {
  pos: 'POS',
  sso: 'SSO',
  public_api: 'Public API',
  outbound_webhooks: 'Outbound webhooks',
  quickbooks_xero: 'QuickBooks / Xero',
  sms_reminders: 'SMS reminders',
  whatsapp_comms: 'WhatsApp comms',
  ip_camera_embed: 'IP camera embed',
  gbp_review_requests: 'Google review requests',
  audit_retention_extended: 'Extended audit retention',
  customer_pwa: 'Customer PWA',
  capacitor_staff_app: 'Native staff app',
  multi_facility: 'Multi-facility',
  custom_portal_domain: 'Custom portal domain',
  live_stay_page: 'Live stay page',
  embed_widget: 'Embed widget',
  daycare_packs: 'Daycare packs',
  marketing_tools: 'Marketing tools',
  calendar_integrations: 'Calendar integrations',
  microchip_lookup: 'Microchip lookup',
  customisation_surfaces: 'Customisation surfaces',
  advanced_reports: 'Advanced reports',
  multiple_kiosks: 'Multiple kiosks',
};

function humaniseFeature(flag: FeatureFlag): string {
  const override = FEATURE_TITLES[flag];
  if (override) return override;
  // Generic fallback: snake_case → "Title-cased words".
  const spaced = flag.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function tierAvailability(flag: FeatureFlag): string {
  const tiers = FEATURE_FLAGS[flag] as ReadonlyArray<string>;
  const onlyEnterprise = tiers.length === 1 && tiers[0] === 'enterprise';
  if (onlyEnterprise) return 'Enterprise';
  if (tiers.includes('pro')) return 'Pro and above';
  return 'Available on a higher plan';
}

/**
 * Card shown in place of a feature the current tier doesn't unlock.
 * Renders a compact muted card with an upgrade CTA. Sits inside the
 * page body — does NOT wrap the AppShell or take over the viewport.
 */
export function UpgradePrompt({ feature }: Props) {
  const title = humaniseFeature(feature);
  const subtitle = tierAvailability(feature);

  return (
    <div className="rounded-2xl border border-border bg-surface px-6 py-12 text-center">
      <div className="mx-auto mb-4 grid size-12 place-items-center rounded-2xl bg-secondary text-muted-foreground">
        <Sparkles className="size-5" strokeWidth={1.75} aria-hidden />
      </div>
      <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
        {subtitle}
      </p>
      <h2 className="mb-2 font-heading text-xl font-medium tracking-tight">{title}</h2>
      <p className="mx-auto mb-6 max-w-md text-sm text-muted-foreground">
        This feature isn't included on your current plan.
      </p>
      <Link
        to="/settings/billing"
        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 press"
      >
        Upgrade your plan
      </Link>
    </div>
  );
}
